import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { BrowserRouter as Router, Route, Switch, useHistory, useLocation } from "react-router-dom";

// axios
// Custom route helpers
import PrivateRoute from "./components/route-helper/PrivateRoute";
import UnauthRoute from "./components/route-helper/UnauthRoute";
import ScrollToTop from "./components/route-helper/ScrollToTop";

// Redux
import { useSelector } from "react-redux";

// Stripe
import "@stripe/stripe-js";

// MUI
import { createTheme, ThemeOptions, ThemeProvider } from "@mui/material";

// TODO: Import these styles from app.scss or index.scss
// custom styles
import "./assets/stylesheets/_typography.scss";
import "./assets/stylesheets/_utilities.scss";
import "./assets/stylesheets/_card.scss";

// Landing pages
// TODO: refactor below code to landing and auth pages
import ForgotPasswordPage from "./components/ForgotPassword/ForgotPasswordPage";
import NewPasswordPage from "./components/ForgotPassword/NewPasswordPage";
import EmailVerificationPage from "./components/VerifyEmail/EmailVerificationPage";

// Dashboard components
import Sidebar from "./components/Dashboard/Sidebar";
import DashboardNav from "./components/DashboardNav";

// Dashboard pages
import DashboardPage from "./components/Dashboard/DashboardPage";
import CalendarPage from "./components/Dashboard/pages/CalendarPage";

// Products
// --- All products
// --- Components
// Purchasing
// --- All purchases
import PurchasesPage from "./components/Dashboard/pages/PurchasingPage/PurchasesPage";

// --- Invoices
// Orders
//Customers page
import UserLogsPage from "./components/Dashboard/pages/UserLogsPage";
import SettingsSidebar from "./components/Dashboard/pages/SettingsPage";

// payment terms
import PaymentTermsPage from "./components/Dashboard/pages/SettingsPage/General/PaymentTerms";

// delivery terms
import DeliveryTermsPage from "./components/Dashboard/pages/SettingsPage/General/DeliveryTerms";

// languages
import LanguagesPage from "./components/Dashboard/pages/SettingsPage/General/Languages";

// error pages
import NotFoundPage from "./components/ErrorPages/NotFoundPage";

//shipment
import SalesOrderLayoutPage from "./components/Dashboard/pages/SettingsPage/Layouts/SalesOrder";

// toast components
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import "./index.css";
import { GlobalStoreState } from "@redux/store";
import getThemePalette from "./mui.config";
import { themeConfig } from "./config/theme";
import DetailsPurchasePage from "./components/Dashboard/pages/PurchasingPage/PurchasesPage/DetailsPage";
import AdjustmentsMainPage from "./components/Dashboard/pages/Inventory/Adjustments/MainPage";
import AdjustmentsCreationPage from "./components/Dashboard/pages/Inventory/Adjustments/CreationPage";
import AdjustmentsViewPage from "./components/Dashboard/pages/Inventory/Adjustments/ViewPage";
import LocationsMainPage from "./components/Dashboard/pages/SettingsPage/General/Locations/MainPage";
import LocationsCreationPage from "./components/Dashboard/pages/SettingsPage/General/Locations/CreationPage";
import LocationsEditingPage from "./components/Dashboard/pages/SettingsPage/General/Locations/EditingPage";
import StockPage from "./components/Dashboard/pages/Inventory/Stock";
import ProductsPage from "./components/Dashboard/pages/Products/MainPage";
import ProductCreationPage from "./components/Dashboard/pages/Products/CreationPage";
import ProductEditingPage from "./components/Dashboard/pages/Products/EditingPage";
import ProductViewPage from "./components/Dashboard/pages/Products/ViewPage";
import TransfersPage from "./components/Dashboard/pages/Inventory/Transfers/MainPage";
import TransfersCreationPage from "./components/Dashboard/pages/Inventory/Transfers/CreationPage";
import TransfersDetailsPage from "./components/Dashboard/pages/Inventory/Transfers/ViewPage";

import CustomersPage from "./components/Dashboard/pages/Customers/Customer/MainPage";
import CustomerCreationPage from "./components/Dashboard/pages/Customers/Customer/CreationPage";
import CustomerEditingPage from "./components/Dashboard/pages/Customers/Customer/EditingPage";
import CustomerDetailsPage from "./components/Dashboard/pages/Customers/Customer/ViewPage";
import CustomerGroupsPage from "./components/Dashboard/pages/Customers/CustomerGroups/MainPage";
import CustomerGroupCreationPage from "./components/Dashboard/pages/Customers/CustomerGroups/CreationPage";
import CustomerGroupEditingPage from "./components/Dashboard/pages/Customers/CustomerGroups/EditingPage";
import CustomerGroupDetailsPage from "./components/Dashboard/pages/Customers/CustomerGroups/ViewPage";

import SuppliersPage from "./components/Dashboard/pages/Suppliers/MainPage";
import SupplierCreationPage from "./components/Dashboard/pages/Suppliers/CreationPage";
import SupplierEditingPage from "./components/Dashboard/pages/Suppliers/EditingPage";
import SupplierDetailsPage from "./components/Dashboard/pages/Suppliers/ViewPage";

import PurchaseCreationPage from "@components/Dashboard/pages/PurchasingPage/PurchasesPage/CreationPage";

import SalesOrdersPage from "@components/Dashboard/pages/Sales/Orders";
import SaleOrderCreationPage from "@components/Dashboard/pages/Sales/Orders/CreationPage";
import SaleOrderEditingPage from "@components/Dashboard/pages/Sales/Orders/EditingPage";
import SaleOrderDetailsPage from "@components/Dashboard/pages/Sales/Orders/DetailsPage";

// import CompanyInformationRouter from "@components/Dashboard/pages/SettingsPage/General/CompanyInfo/CompanyInformationRouter";
// import CompanyInformationPage from "@components/Dashboard/pages/SettingsPage/General/Company";
import RestockingPage from "@components/Dashboard/DashboardPage/subpages/RestockingPage";
import ReceiveMainPage from "@components/Dashboard/pages/PurchasingPage/Receive/MainPage";
import ReceiveCreationPage from "@components/Dashboard/pages/PurchasingPage/Receive/CreationPage";
import ReceiveViewPage from "@components/Dashboard/pages/PurchasingPage/Receive/ViewPage";
import MyAccountRouter from "@components/Dashboard/pages/SettingsPage/General/MyAccount/MyAccountRouter";
import TaxRatesPage from "./components/Dashboard/pages/SettingsPage/General/TaxRatesPage";
import PackagingPage from "./components/Dashboard/pages/SettingsPage/General/Packaging";
import CategoriesPage from "./components/Dashboard/pages/SettingsPage/General/Categories";
import PickingMainPage from "@components/Dashboard/pages/Sales/Picking/MainPage";
import PickingCreationPage from "src/components/Dashboard/pages/Sales/Picking/CreationPage";
import PickingDetailsPage from "@components/Dashboard/pages/Sales/Picking/ViewPage";
import {
    ThirdPartyIntegrationsPage
} from "@components/Dashboard/pages/SettingsPage/Integrations/ThirdPartyIntegrations/MainPage";
import { useAbility } from "@casl/react";
import { AbilityContext, updateAbility } from "./casl.config";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import StockCountPage from "./components/Dashboard/pages/Inventory/StockCount";
import UnitsPage from "./components/Dashboard/pages/SettingsPage/General/Units";
import CompanyInformationPage
    from "./components/Dashboard/pages/SettingsPage/General/CompanyInfo/CompanyInformationRouter";
import CurrenciesPage from "./components/Dashboard/pages/SettingsPage/General/Currencies";
import PurchaseOrderEditingPage from "@components/Dashboard/pages/PurchasingPage/PurchasesPage/EditingPage";
import CollectionsMainPage from "@components/Dashboard/pages/Inventory/Collections/MainPage";
import CollectionsCreationPage from "@components/Dashboard/pages/Inventory/Collections/CreationPage";
import CollectionsEditingPage from "@components/Dashboard/pages/Inventory/Collections/EditingPage";
import FeedPage from "@components/Dashboard/DashboardPage/subpages/FeedPage";
import BOMPage from "./components/Dashboard/pages/BOM/MainPage";
import BOMEditingPage from "./components/Dashboard/pages/BOM/EditingPage";
import BOMCreationPage from "./components/Dashboard/pages/BOM/CreationPage";
import BOMDetailsPage from "./components/Dashboard/pages/BOM/DetailsPage";
import ProducingHistoryPage from "./components/Dashboard/pages/BOM/ProducingHistoryPage";
import SignUpPage from "./components/SignUp";
import VerifyEmailPage from "./components/SignUp/modals/VerifyEmailPage";
import CustomFieldsPage from "@components/Dashboard/pages/SettingsPage/General/CustomFields";
import { toastError, useEchoEffect } from "@helpers/utils";
import { SocketRoleUpdated } from "./types/general";
import { setCompany, setRole, setUser, switchCompany } from "@redux/features/auth/authSlice";
import Changelog from "@components/Changelog";
import LoginPage from "@components/LoginPage";
import FullscreenCompanySwitcher from "./components/Dashboard/pages/FullscreenCompanySwitcher";
import { internalApiSlice } from "@redux/api/internalApiSlice";
import { companiesApi } from "@redux/features/companies/companiesApi";
import { authApi } from "@redux/features/auth/authApi";
import DomainGuard from "@/DomainGuard";
import InvitationPage from "./components/InvitationPage";
import DefaultsPage from "@components/Dashboard/pages/SettingsPage/General/Defaults";

const env = import.meta.env.VITE_ENV;

const DashboardContent: React.FC<{ url: string }> = ({ url }) => {
    const location = useLocation();
    const shouldShowNav = location.pathname === "/dashboard/main" || location.pathname.startsWith("/dashboard/main/");

    return (
        <>
            <Changelog />
            <main
                className="main"
                style={{
                    position: "relative",
                    padding: "0"
                }}
            >
                <div className="ellipse-left"></div>
                <div className="ellipse-right"></div>
                <div className="background-blur">
                    <div className="flex flex-row">
                        <Sidebar minifyOnURLs={["/dashboard/settings"]} />
                        <PrivateRoute
                            path={`${url}/settings`}
                            component={SettingsSidebar}
                        />
                        {/*<div className={`w-[calc(100%-${window.location.pathname.includes("settings") ? 84 : 302}px)] pt-[56px] 2xl:px-[50px] sm:px-[10px]`}>*/}

                        <div
                            className={`w-[calc(100%-302px)] pt-[40px] 2xl:px-[50px] sm:px-[10px]`}
                        >
                            {shouldShowNav && <DashboardNav />}

                            {/* DASHBOARD */}
                            <PrivateRoute
                                path={`${url}/main`}
                                component={DashboardPage}
                                exact
                            />

                            <PrivateRoute
                                path={`${url}/main/feed`}
                                component={FeedPage}
                                exact
                            />

                            <PrivateRoute
                                path={`${url}/main/restocking`}
                                component={RestockingPage}
                                exact
                            />

                            {/* CALENDAR */}
                            <PrivateRoute
                                path={`${url}/calendar`}
                                component={CalendarPage}
                                exact
                            />

                            {/* PRODUCTS */}

                            {/* Products / All */}
                            <PrivateRoute
                                path={`${url}/products`}
                                component={ProductsPage}
                                exact
                            />
                            {/* Products / New */}
                            <PrivateRoute
                                path={`${url}/products/new`}
                                component={ProductCreationPage}
                                exact
                            />
                            <PrivateRoute
                                path={`${url}/products/:id/edit/:page?`}
                                component={ProductEditingPage}
                                exact
                            />

                            <PrivateRoute
                                path={`${url}/products/:id/details/:page?`}
                                component={ProductViewPage}
                                exact
                            />

                            {/* Bill Of Materials / All */}
                            <PrivateRoute
                                path={`${url}/bill-of-materials`}
                                component={BOMPage}
                                exact
                            />

                            {/* Bill Of Materials / New */}
                            <PrivateRoute
                                path={`${url}/bill-of-materials/new`}
                                component={BOMCreationPage}
                                exact
                            />

                            {/* Bill Of Materials / Edit */}
                            <PrivateRoute
                                path={`${url}/bill-of-materials/:id/edit`}
                                component={BOMEditingPage}
                                exact
                            />

                            <PrivateRoute
                                path={`${url}/bill-of-materials/:id/details`}
                                component={BOMDetailsPage}
                                exact
                            />

                            <PrivateRoute
                                path={`${url}/bill-of-materials/production-history`}
                                component={ProducingHistoryPage}
                                exact
                            />

                            {/* All Purchases */}
                            <PrivateRoute
                                path={`${url}/purchasing`}
                                component={PurchasesPage}
                                exact
                            />
                            {/* New Purchases */}
                            <PrivateRoute
                                path={`${url}/purchasing/new`}
                                component={PurchaseCreationPage}
                                exact
                            />

                            <PrivateRoute
                                path={`${url}/purchasing/:id/details`}
                                component={DetailsPurchasePage}
                                exact
                            />

                            <PrivateRoute
                                path={`${url}/purchasing/:id/edit`}
                                component={PurchaseOrderEditingPage}
                                exact
                            />

                            <PrivateRoute
                                path={`${url}/receive`}
                                component={ReceiveMainPage}
                                exact
                            />

                            <PrivateRoute
                                path={`${url}/receive/new`}
                                component={ReceiveCreationPage}
                                exact
                            />

                            <PrivateRoute
                                path={`${url}/receive/:id/details`}
                                component={ReceiveViewPage}
                                exact
                            />

                            {/* INVENTORY/ADJUSTMENTS */}
                            <PrivateRoute
                                path={`${url}/inventory/adjustments`}
                                component={AdjustmentsMainPage}
                                exact
                            />

                            <PrivateRoute
                                path={`${url}/inventory/adjustments/new`}
                                component={AdjustmentsCreationPage}
                                exact
                            />

                            <PrivateRoute
                                path={`${url}/inventory/adjustments/:id/details`}
                                component={AdjustmentsViewPage}
                                exact
                            />

                            {/* INVENTORY/COLLECTIONS */}

                            <PrivateRoute
                                path={`${url}/inventory/collections`}
                                component={CollectionsMainPage}
                                exact
                            />

                            <PrivateRoute
                                path={`${url}/inventory/collections/new`}
                                component={CollectionsCreationPage}
                                exact
                            />

                            <PrivateRoute
                                path={`${url}/inventory/collections/:id/edit`}
                                component={CollectionsEditingPage}
                                exact
                            />

                            {/* INVENTORY/STOCK */}
                            <PrivateRoute
                                path={`${url}/inventory/stock`}
                                component={StockPage}
                                exact
                            />

                            <PrivateRoute
                                path={`${url}/inventory/stock-count`}
                                component={StockCountPage}
                                exact
                            />

                            {/* LOCATIONS */}
                            <PrivateRoute
                                path={`${url}/settings/locations`}
                                component={LocationsMainPage}
                                exact
                            />

                            <PrivateRoute
                                path={`${url}/settings/locations/new`}
                                component={LocationsCreationPage}
                                exact
                            />

                            <PrivateRoute
                                path={`${url}/settings/locations/:id/edit`}
                                component={LocationsEditingPage}
                                exact
                            />

                            <PrivateRoute
                                path={`${url}/settings/defaults`}
                                component={DefaultsPage}
                                exact
                            />

                            <PrivateRoute
                                path="/dashboard/settings/third-party"
                                component={ThirdPartyIntegrationsPage}
                                exact
                            />

                            <PrivateRoute
                                path="/dashboard/settings/units"
                                component={UnitsPage}
                                exact
                            />

                            <PrivateRoute
                                path="/dashboard/settings/currencies"
                                component={CurrenciesPage}
                                exact
                            />

                            {/* Stock transfers pages */}
                            <PrivateRoute
                                path={`${url}/inventory/transfers`}
                                component={TransfersPage}
                                exact
                            />

                            <PrivateRoute
                                path={`${url}/inventory/transfers/new`}
                                component={TransfersCreationPage}
                                exact
                            />

                            <PrivateRoute
                                path={`${url}/inventory/transfers/:id/details`}
                                component={TransfersDetailsPage}
                                exact
                            />

                            {/* SALE ORDERS */}
                            <PrivateRoute
                                path={`${url}/sales/orders`}
                                component={SalesOrdersPage}
                                exact
                            />

                            <PrivateRoute
                                path={`${url}/sales/orders/new`}
                                component={SaleOrderCreationPage}
                                exact
                            />

                            <PrivateRoute
                                path={`${url}/sales/orders/:id/details`}
                                component={SaleOrderDetailsPage}
                                exact
                            />

                            <PrivateRoute
                                path={`${url}/sales/orders/:id/edit`}
                                component={SaleOrderEditingPage}
                                exact
                            />

                            {/*PICKING*/}
                            <PrivateRoute
                                path={`${url}/picking`}
                                component={PickingMainPage}
                                exact
                            />

                            <PrivateRoute
                                path={`${url}/picking/new`}
                                component={PickingCreationPage}
                                exact
                            />

                            <PrivateRoute
                                path={`${url}/picking/:id/details`}
                                component={PickingDetailsPage}
                                exact
                            />

                            {/* Customers */}
                            <PrivateRoute
                                path={`${url}/customers`}
                                component={CustomersPage}
                                exact
                            />

                            <PrivateRoute
                                path={`${url}/customers/new/:page?`}
                                component={CustomerCreationPage}
                                exact
                            />

                            <PrivateRoute
                                path={`${url}/customers/:id/edit/:page?`}
                                component={CustomerEditingPage}
                                exact
                            />

                            <PrivateRoute
                                path={`${url}/customers/:id/details/:page?`}
                                component={CustomerDetailsPage}
                                exact
                            />

                            {/* Customers Groups */}
                            <PrivateRoute
                                path={`${url}/customer-groups`}
                                component={CustomerGroupsPage}
                                exact
                            />

                            <PrivateRoute
                                path={`${url}/customer-groups/new`}
                                component={CustomerGroupCreationPage}
                                exact
                            />

                            <PrivateRoute
                                path={`${url}/customer-groups/:id/edit`}
                                component={CustomerGroupEditingPage}
                                exact
                            />

                            <PrivateRoute
                                path={`${url}/customer-groups/:id/details`}
                                component={CustomerGroupDetailsPage}
                                exact
                            />

                            {/* Suppliers */}
                            <PrivateRoute
                                path={`${url}/suppliers`}
                                component={SuppliersPage}
                                exact
                            />

                            <PrivateRoute
                                path={`${url}/suppliers/new/:page?`}
                                component={SupplierCreationPage}
                                exact
                            />

                            <PrivateRoute
                                path={`${url}/suppliers/:id/edit/:page?`}
                                component={SupplierEditingPage}
                                exact
                            />

                            <PrivateRoute
                                path={`${url}/suppliers/:id/details/:page?`}
                                component={SupplierDetailsPage}
                                exact
                            />

                            {/* USER-LOGS */}
                            <PrivateRoute
                                path={`${url}/user-logs`}
                                component={UserLogsPage}
                                exact
                            />
                            {/* //* Settings pages */}
                            <Route
                                path={`${url}/settings`}
                                render={({ match: { url } }) => (
                                    <>
                                        {/* GENERAL SETTINGS */}
                                        <PrivateRoute
                                            path={`${url}/company/:page?`}
                                            component={CompanyInformationPage}
                                        />

                                        {/* PAYMENT TERMS */}
                                        <PrivateRoute
                                            path={`${url}/general/payment-terms`}
                                            component={PaymentTermsPage}
                                            exact
                                        />

                                        {/* DELIVERY TERMS */}
                                        <PrivateRoute
                                            path={`${url}/general/delivery-terms`}
                                            component={DeliveryTermsPage}
                                            exact
                                        />

                                        {/* DELIVERY TERMS */}
                                        <PrivateRoute
                                            path={`${url}/general/tax`}
                                            component={TaxRatesPage}
                                            exact
                                        />

                                        {/* PACKAGING */}
                                        <PrivateRoute
                                            path={`${url}/general/packaging`}
                                            component={PackagingPage}
                                            exact
                                        />

                                        {/* CATEGORIES */}
                                        <PrivateRoute
                                            path={`${url}/general/categories`}
                                            component={CategoriesPage}
                                            exact
                                        />

                                        {/* LANGUAGES */}
                                        <PrivateRoute
                                            path={`${url}/general/languages`}
                                            component={LanguagesPage}
                                            exact
                                        />

                                        {/* CUSTOM FIELDS */}
                                        <PrivateRoute
                                            path={`${url}/general/custom-fields`}
                                            component={CustomFieldsPage}
                                            exact
                                        />

                                        {/* SALES ORDER LAYOUT  PAGE */}
                                        <PrivateRoute
                                            path={`${url}/layouts/sales-order`}
                                            component={SalesOrderLayoutPage}
                                            exact
                                        />
                                        <PrivateRoute
                                            path={`${url}/general/account/:page?`}
                                            component={MyAccountRouter}
                                        />
                                    </>
                                )}
                            />

                            <div style={{ height: "40px" }}></div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};

const App: React.FC = () => {
    // Workaround to load permissions from persisted auth slice after page reload
    const ability = useAbility(AbilityContext);
    const user = useAppSelector((state) => state.auth.user);

    const dispatch = useAppDispatch();

    const [roleId, setRoleId] = React.useState<number | undefined>(user?.role.id);

    const permissions = useAppSelector(
        (state) => state.auth.user?.role.permissions
    );

    useEffect(() => {
        console.log("Permissions:", permissions);
    }, [permissions]);

    if (!ability.rules.length && permissions) updateAbility(permissions);

    useEchoEffect(
        `role.${roleId ?? user?.role.id}.${user?.id}`,
        "RoleUpdatedEvent",
        (data: SocketRoleUpdated) => {
            console.log("RoleUpdateEvent", data);
            // Update role id if it has changed
            // if (data.id !== roleId) {
            //   setRoleId(data.id);
            //   dispatch(setRole({
            //     id: data.id,
            //     name: data.name,
            //     permissions: data.permissions,
            //   }));
            // }
            setRoleId(data.id);
            dispatch(setRole({
                id: data.id,
                name: data.name,
                permissions: data.permissions
            }));
            updateAbility(data.permissions);
        },
        undefined,
        [user, roleId]
    );

    // ! Theme updating is done via Redux Toolkit, not the approach, suggested by MUI library
    const themeMode = useSelector((state: GlobalStoreState) => state.theme.theme);
    const theme = useMemo(
        () =>
            createTheme({
                ...themeConfig,
                ...getThemePalette(themeMode)
            } as ThemeOptions),
        [themeMode]
    );


    return (
        <ThemeProvider theme={theme}>
            <Router>
                <PersonalInformationListener />

                <DomainGuard />
                <ScrollToTop>
                    <Switch>
                        <UnauthRoute path="/login" exact>
                            <LoginPage />
                        </UnauthRoute>

                        <UnauthRoute path="/registration" exact>
                            <SignUpPage />
                        </UnauthRoute>

                        <UnauthRoute path="/verify-email" exact>
                            <VerifyEmailPage />
                        </UnauthRoute>

                        <UnauthRoute path="/forgot-password" exact>
                            <ForgotPasswordPage />
                        </UnauthRoute>

                        <UnauthRoute path="/reset-password" exact>
                            <NewPasswordPage />
                        </UnauthRoute>

                        <UnauthRoute path="/invite/:id" exact>
                            <InvitationPage />
                        </UnauthRoute>

                        <PrivateRoute path="/email-verify">
                            <EmailVerificationPage />
                        </PrivateRoute>

                        <PrivateRoute path="/select-company" exact component={FullscreenCompanySwitcher} />

                        {/* Dashboard routes */}
                        <Route
                            path="/dashboard"
                            render={({ match: { url } }) => (
                                <DashboardContent url={url} />
                            )}
                        />

                        {/* 404 catch route */}
                        <Route path="*">
                            <NotFoundPage />
                        </Route>
                    </Switch>
                </ScrollToTop>
            </Router>
            <ToastContainer position="top-center" />
        </ThemeProvider>
    );
};

function PersonalInformationListener() {
    const history = useHistory();
    const dispatch = useAppDispatch();

    const user = useAppSelector((state) => state.auth.user);
    const activeCompany = useAppSelector((state) => state.auth.user?.company);

    const wasReinitialized = useRef(false);

    const reinitializePersonalInformation = useCallback(async () => {
        if (!user) return;

        try {
            console.log("Reinitializing personal information...");
            let meQuery = dispatch(authApi.endpoints.getMe.initiate(undefined, {
                forceRefetch: true,
                subscribe: false
            }));
            const meData = await meQuery.unwrap();

            let companyQuery = dispatch(companiesApi.endpoints.getCompany.initiate(meData.company.id, {
                forceRefetch: true,
                subscribe: false
            }));
            const companyData = await companyQuery.unwrap();

            const previousCompanyId = user.company?.id;

            dispatch(setUser({
                ...meData,
                company: companyData
            }));

            if (previousCompanyId != companyData.id) {
                dispatch(internalApiSlice.util.resetApiState());
            }

        } catch (e) {
            console.log("Error while reinitializing personal information:", e);
            toastError(e as any);
        }
    }, [user]);

    useEffect(() => {
        if (user && !wasReinitialized.current) {
            reinitializePersonalInformation().then(() => {
                wasReinitialized.current = true;
            });
        }
    }, [user, wasReinitialized, reinitializePersonalInformation]);

    useEffect(() => {
        if (user && !activeCompany && history.location.pathname !== "/select-company") {
            history.push("/select-company");
        }
    }, [user, activeCompany, history]);

    /**
     * Tracking inbound company blocks via Echo
     */
    useEchoEffect<{ company_id: number; is_blocked: true }>(
        `default_company.${user?.id ?? -1}`,
        "CompanyBlockStatusChangeEvent",
        async (data) => {
            await dispatch(companiesApi.endpoints.getCompanies.initiate(undefined, { forceRefetch: true }));

            if (user && activeCompany && data.company_id === activeCompany?.id && data.is_blocked) {
                dispatch(setCompany(undefined));
            }

        },
        undefined,
        [user, activeCompany]
    );

    /**
     * The `latestSwitchedToCompanyId` is used to prevent race conditions when switching companies.
     * The latest switch will always win this race - for this I utilize the `processSwitch` function.
     */

    const latestSwitchedToCompanyId = useRef<number | null>(null);
    const switchingCompany = useRef<boolean>(false);

    const processSwitch = async (company_id: number) => {
        if (user && company_id !== activeCompany?.id) {
            switchingCompany.current = true;

            try {
                await dispatch(switchCompany(company_id));
            } finally {
                switchingCompany.current = false;

                if (latestSwitchedToCompanyId.current != null && latestSwitchedToCompanyId.current !== company_id) {
                    const nextId = latestSwitchedToCompanyId.current;
                    latestSwitchedToCompanyId.current = null;
                    await processSwitch(nextId);
                }
            }
        }
    };

    useEchoEffect<{ new_company_id: number }>(
        `default_company.${user?.id ?? -1}`,
        "DefaultCompanyChangedEvent",
        (data) => {
            if (switchingCompany.current) {
                latestSwitchedToCompanyId.current = data.new_company_id;
            } else {
                processSwitch(data.new_company_id);
            }
        },
        undefined,
        [user, activeCompany]
    );

    return null;
}

export default App;
